import React, { useState, useEffect } from 'react'
import { Search, RefreshCw, ChevronDown, ChevronRight, Download } from 'lucide-react'
import { useAdminApi } from '../../contexts/AdminApiContext'
import { CSVExporter } from '../../utils/csvExport'
import './TableViewer.css'

interface TableViewerProps {
  tableName: 'users' | 'books' | 'chapters' | 'api_usage'
}

const TableViewer: React.FC<TableViewerProps> = ({ tableName }) => {
  const [data, setData] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [searchTerm, setSearchTerm] = useState('')
  const [sortColumn, setSortColumn] = useState<string | null>(null)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set())
  const [isDownloading, setIsDownloading] = useState(false)
  const { apiService, isReady } = useAdminApi()

  useEffect(() => {
    if (isReady) {
      loadData()
    }
  }, [tableName, isReady])

  const loadData = async () => {
    setLoading(true)
    setError(null)
    
    try {
      let result
      switch (tableName) {
        case 'users':
          result = await apiService.getUsers()
          break
        case 'books':
          result = await apiService.getBooks()
          break
        case 'chapters':
          result = await apiService.getChapters()
          break
        case 'api_usage':
          result = await apiService.getApiUsage()
          break
        default:
          throw new Error(`Unknown table: ${tableName}`)
      }
      
      setData(Array.isArray(result) ? result : [result])
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load data')
      console.error(`Error loading ${tableName}:`, err)
    } finally {
      setLoading(false)
    }
  }

  const formatValue = (value: any, key?: string, truncate: boolean = true): string => {
    if (value === null || value === undefined) {
      return '—'
    }
    if (typeof value === 'boolean') {
      return value ? '✓' : '✗'
    }
    if (key && key.includes('date') || key && key.includes('timestamp')) {
      try {
        return new Date(value).toLocaleString()
      } catch {
        return String(value)
      }
    }
    if (key && key.includes('cost') && typeof value === 'number') {
      return `$${value.toFixed(4)}`
    }
    if (typeof value === 'string' && truncate && value.length > 50) {
      return value.substring(0, 50) + '...'
    }
    if (typeof value === 'object') {
      return JSON.stringify(value, null, 2)
    }
    return String(value)
  }

  const formatColumnName = (column: string): string => {
    return column
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ')
  }

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    } else {
      setSortColumn(column)
      setSortDirection('asc')
    }
  }

  const toggleRowExpansion = (id: string) => {
    const newExpanded = new Set(expandedRows)
    if (newExpanded.has(id)) {
      newExpanded.delete(id)
    } else {
      newExpanded.add(id)
    }
    setExpandedRows(newExpanded)
  }

  const getSortedAndFilteredData = () => {
    let filteredData = data.filter(item => {
      const searchLower = searchTerm.toLowerCase()
      return Object.values(item).some(value =>
        String(value).toLowerCase().includes(searchLower)
      )
    })

    if (sortColumn) {
      filteredData = [...filteredData].sort((a, b) => {
        const aValue = a[sortColumn]
        const bValue = b[sortColumn]
        
        if (aValue === null || aValue === undefined) return 1
        if (bValue === null || bValue === undefined) return -1
        
        const comparison = aValue < bValue ? -1 : aValue > bValue ? 1 : 0
        return sortDirection === 'asc' ? comparison : -comparison
      })
    }

    return filteredData
  }

  const getMainColumns = () => {
    if (!data.length) return []
    const columns = Object.keys(data[0])
    
    // Table-specific column configurations
    const tableConfigs = {
      'users': {
        priorities: { 'id': 1, 'name': 2, 'email': 3, 'created_at': 4, 'updated_at': 5 },
        hidden: ['description'],
        maxColumns: 5
      },
      'books': {
        priorities: { 'id': 1, 'title': 2, 'genre': 3, 'status': 4, 'target_age': 5, 'created_at': 6 },
        hidden: ['description', 'logline', 'trailer'],
        maxColumns: 6
      },
      'chapters': {
        priorities: { 'id': 1, 'title': 2, 'book_title': 3, 'order_index': 4, 'audio_file': 5, 'created_at': 6 },
        hidden: ['book_id'],
        maxColumns: 6
      },
      'api_usage': {
        priorities: { 'id': 1, 'api_provider': 2, 'model': 3, 'api_endpoint': 4, 'actual_cost_usd': 5, 'status': 6, 'request_timestamp': 7 },
        hidden: ['request_metadata', 'response_metadata'],
        maxColumns: 7
      }
    }
    
    const config = tableConfigs[tableName as keyof typeof tableConfigs] || {
      priorities: { 'id': 1, 'name': 2, 'title': 2, 'email': 3, 'status': 4, 'created_at': 5 },
      hidden: ['description', 'request_metadata', 'response_metadata'],
      maxColumns: 6
    }
    
    return columns
      .filter(col => !config.hidden.includes(col))
      .sort((a, b) => (config.priorities[a as keyof typeof config.priorities] || 99) - (config.priorities[b as keyof typeof config.priorities] || 99))
      .slice(0, config.maxColumns)
  }

  const getDetailColumns = () => {
    if (!data.length) return []
    const allColumns = Object.keys(data[0])
    const mainColumns = getMainColumns()
    return allColumns.filter(col => !mainColumns.includes(col))
  }

  const handleDownloadCSV = async () => {
    if (!data.length) return
    
    setIsDownloading(true)
    try {
      // Use original data (not filtered) for complete export
      const exportData = sortedAndFilteredData.length > 0 ? sortedAndFilteredData : data
      
      CSVExporter.downloadCSV(exportData, tableName, {
        filename: `voyager_${tableName}`,
        includeTimestamp: true,
        // Exclude very verbose fields from CSV
        excludeColumns: ['request_metadata', 'response_metadata'],
        dateFormat: 'locale'
      })
    } catch (error) {
      console.error('Failed to download CSV:', error)
      // TODO: Add toast notification for error
    } finally {
      setIsDownloading(false)
    }
  }

  if (loading) {
    return (
      <div className="table-viewer">
        <div className="loading-state">
          <RefreshCw className="loading-spinner" size={24} />
          <span>Loading {tableName} data...</span>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="table-viewer">
        <div className="error-state">
          <span>⚠️ Error loading {tableName}: {error}</span>
          <button className="btn btn-secondary" onClick={loadData}>
            Retry
          </button>
        </div>
      </div>
    )
  }

  if (!data || data.length === 0) {
    return (
      <div className="table-viewer">
        <div className="empty-state">
          <span>No {tableName} data found</span>
        </div>
      </div>
    )
  }

  const sortedAndFilteredData = getSortedAndFilteredData()
  const mainColumns = getMainColumns()
  const detailColumns = getDetailColumns()

  return (
    <div className="table-viewer">
      <div className="table-header">
        <div className="table-info">
          <span className="record-count">
            {sortedAndFilteredData.length} of {data.length} records
          </span>
        </div>
        
        <div className="table-actions">
          <div className="search-box">
            <Search size={16} />
            <input
              type="text"
              placeholder={`Search ${tableName}...`}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="action-buttons">
            <button 
              className="btn btn-secondary download-btn"
              onClick={handleDownloadCSV}
              disabled={isDownloading || !data.length}
              title={`Download ${tableName} as CSV (${CSVExporter.getEstimatedSize(data)})`}
            >
              <Download size={16} className={isDownloading ? 'downloading' : ''} />
              {isDownloading ? 'Downloading...' : 'CSV'}
            </button>
            <button className="btn btn-secondary" onClick={loadData}>
              <RefreshCw size={16} />
              Refresh
            </button>
          </div>
        </div>
      </div>
      
      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th className="expand-column"></th>
              {mainColumns.map(column => (
                <th 
                  key={column} 
                  className={`sortable ${sortColumn === column ? 'sorted' : ''}`}
                  onClick={() => handleSort(column)}
                >
                  <div className="th-content">
                    <span>{formatColumnName(column)}</span>
                    {sortColumn === column && (
                      <span className={`sort-indicator ${sortDirection}`}>
                        {sortDirection === 'asc' ? '↑' : '↓'}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sortedAndFilteredData.map((row, index) => {
              const rowId = row.id || `row-${index}`
              const isExpanded = expandedRows.has(rowId)
              
              return (
                <React.Fragment key={rowId}>
                  <tr className="data-row">
                    <td className="expand-cell" onClick={() => toggleRowExpansion(rowId)}>
                      {detailColumns.length > 0 && (
                        isExpanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />
                      )}
                    </td>
                    {mainColumns.map(column => (
                      <td key={column} className="data-cell">
                        <div className="cell-content">
                          {formatValue(row[column], column, true)}
                        </div>
                      </td>
                    ))}
                  </tr>
                  
                  {isExpanded && detailColumns.length > 0 && (
                    <tr className="detail-row">
                      <td></td>
                      <td colSpan={mainColumns.length} className="detail-cell">
                        <div className="detail-content">
                          <h4>Additional Details</h4>
                          <div className="detail-grid">
                            {detailColumns.map(column => (
                              <div key={column} className="detail-item">
                                <span className="detail-label">
                                  {formatColumnName(column)}:
                                </span>
                                <span className="detail-value">
                                  {formatValue(row[column], column, false)}
                                </span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              )
            })}
          </tbody>
        </table>
      </div>
    </div>
  )
}

export default TableViewer